import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useListingContext } from '../../../contexts/ListingContext';
import styles from '../../../styles/ListingManagement.module.css';
import type { ListingFilters, ListingSummary } from '../../../types/ListingTypes';
import ListStats from './../ListingManagement/ListStats';
import ListFilterBar from './ListFilterBar';
import ListingTable from './../ListingManagement/ListingTable';
import ListingManagementSkeleton from './../ListingManagement/ListingManagementSkeleton';

interface AgencyListingsProps {
  agencyId: string;
}

const AgencyListings: React.FC<AgencyListingsProps> = ({ agencyId }) => {
  const { fetchAgencyListings } = useListingContext();

  const [listings, setListings] = useState<ListingSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ListingFilters>({});

  useEffect(() => {
    if (!agencyId) return;
    setLoading(true);
    setError(null);
    fetchAgencyListings(agencyId)
      .then(setListings)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load agency listings';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [agencyId, fetchAgencyListings]);

  const filtered = useMemo(() => {
    return listings.filter((l) => {
      if (filters.type && l.type !== filters.type) return false;
      if (filters.status && l.status !== filters.status) return false;
      if (filters.city && !l.city.toLowerCase().includes(filters.city.toLowerCase())) return false;
      if (filters.address) {
        const addr = [
          l.complexName,
          l.unitNumber ? `Unit ${l.unitNumber}` : undefined,
          l.streetNumber,
          l.streetName,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!addr.includes(filters.address.toLowerCase())) return false;
      }
      return true;
    });
  }, [listings, filters]);

  const handleFilterChange = (key: keyof ListingFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  if (loading && listings.length === 0) {
    return <ListingManagementSkeleton title="Agency Listings" subtitle="View and track all property listings across your agency" />;
  }

  return (
    <div className={styles.listingManagementContainer}>
      <div className={styles.listingManagementContent}>
        <div className={styles.listingManagementHeader}>
          <h1 className={styles.listingManagementTitle}>Agency Listings</h1>
          <p className={styles.listingManagementSubtitle}>View and track all property listings across your agency</p>
        </div>

        <ListStats listings={listings} />

        <div className={styles.listingManagementSection}>
          <div className={styles.listingManagementSectionHeader}>
            <ListFilterBar filters={filters} handleFilterChange={handleFilterChange}/>
          </div>
          <ListingTable listings={filtered} loading={loading} error={error}/>
        </div>
      </div>
    </div>
  );
};

export default AgencyListings;
